import { NormativaI } from "interfaces/Normativa";
import { DiseñoCurricularI } from "interfaces/DiseñoCurricular";
import { DocumentacionAdjuntaI } from "dtos/documentacionAdjunta";
import { EntityID } from "api/types/EntityID";
import {
  postNormativa,
  putNormativa,
} from "./normativas";
import {
  postDiseñoCurricular,
  putDiseñoCurricular,
} from "./diseñoCurricular";

// ─── Tipos compartidos ──────────────────────────────────────────────────────

type FakeFile = File & {
  fakeId: EntityID;
  isVirtual: boolean;
  nombre_real?: string;
};

type NormativaAdjuntos = Partial<Record<"documentacion_adjunta" | "anexos", File[]>>;
type DisenioCurricularAdjuntos = Partial<Record<"documentacion_adjunta", File[]>>;

// ─── Constantes ─────────────────────────────────────────────────────────────

/** Máximo de anexos que acepta el interceptor de Normativas por petición */
const NORMATIVA_BATCH_SIZE_ANEXOS = 5;
/** Máximo de documentos que acepta el interceptor de Diseño Curricular por petición */
const DISENIO_BATCH_SIZE_DOCS = 10;
/** Cantidad de reintentos ante un fallo de lote */
const MAX_RETRIES = 1;

// ─── Utilidades ─────────────────────────────────────────────────────────────

/** Divide un array en sub-arrays de tamaño `size` */
function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

// ─── Error personalizado de lotes fallidos ──────────────────────────────────

/** Información de un lote que falló después del reintento */
export interface FailedBatch {
  /** Índice del lote (0-based) */
  batchIndex: number;
  /** Nombres de los archivos del lote que falló */
  fileNames: string[];
  /** Error original */
  error: unknown;
}

/**
 * Error personalizado que se lanza cuando uno o más lotes fallan
 * después del reintento. Contiene la lista de lotes fallidos con
 * los nombres de archivos involucrados.
 */
export class BatchUploadError extends Error {
  public readonly failedBatches: FailedBatch[];

  constructor(failedBatches: FailedBatch[]) {
    const fileList = failedBatches
      .flatMap((b) => b.fileNames)
      .join(", ");

    super(
      `Falló la subida de ${failedBatches.length} lote(s). ` +
      `Archivos afectados: ${fileList}`
    );

    this.name = "BatchUploadError";
    this.failedBatches = failedBatches;
  }
}

// ─── Ejecutor de lote con reintento ─────────────────────────────────────────

/**
 * Ejecuta una función asíncrona. Si falla, la reintenta `MAX_RETRIES` veces.
 * Si sigue fallando, retorna el error en vez de lanzarlo para que el flujo
 * continúe y se acumulen los lotes fallidos.
 */
async function executeWithRetry<T>(
  fn: () => Promise<T>,
  batchIndex: number,
  files: File[],
): Promise<{ success: true; result: T } | { success: false; failure: FailedBatch }> {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const result = await fn();
      return { success: true, result };
    } catch (error) {
      const isLastAttempt = attempt === MAX_RETRIES;
      if (isLastAttempt) {
        console.error(
          `[BatchUpload] Lote ${batchIndex + 1} falló después de ${MAX_RETRIES + 1} intento(s). ` +
          `Archivos: ${files.map((f) => f.name).join(", ")}`,
          error
        );
        return {
          success: false,
          failure: {
            batchIndex,
            fileNames: files.map((f) => f.name),
            error,
          },
        };
      }
      console.warn(
        `[BatchUpload] Lote ${batchIndex + 1} falló (intento ${attempt + 1}/${MAX_RETRIES + 1}), reintentando...`
      );
    }
  }
  // Inalcanzable, pero TypeScript lo necesita
  throw new Error("Unreachable");
}

// ═══════════════════════════════════════════════════════════════════════════
//  NORMATIVAS
// ═══════════════════════════════════════════════════════════════════════════

interface NormativaCreateBatchParams {
  /** Parámetros del formulario (sin documentacion_adjunta ni anexos) */
  formParams: Record<string, any>;
  /** Archivo de documentación adjunta (max 1) */
  documentacionAdjunta: File[];
  /** Todos los anexos nuevos a subir */
  anexos: File[];
}

/**
 * Crea una normativa subiendo los anexos por lotes.
 *
 * Lote 1 (POST): params + doc_adjunta + primeros 5 anexos → crea la entidad
 * Lote N (PUT):  params + id + siguientes 5 anexos → agrega al existente
 *
 * Cada lote tiene 1 reintento automático ante fallos. Si aún falla,
 * se registra y al final se lanza BatchUploadError con los archivos fallidos.
 */
export async function createNormativaBatch({
  formParams,
  documentacionAdjunta,
  anexos,
}: NormativaCreateBatchParams): Promise<NormativaI> {
  const anexosBatches = chunkArray(anexos, NORMATIVA_BATCH_SIZE_ANEXOS);
  const failedBatches: FailedBatch[] = [];

  // ── Lote 1: POST (sin reintento: si falla el create no hay entidad) ───
  const firstBatchAnexos = anexosBatches.length > 0 ? anexosBatches[0] : [];

  const images: NormativaAdjuntos = {
    documentacion_adjunta: documentacionAdjunta,
  };
  if (firstBatchAnexos.length > 0) {
    images.anexos = firstBatchAnexos;
  }

  const newNormativa = await postNormativa({
    params: formParams,
    images,
  });
  console.log(`[BatchUpload] Lote 1 enviado con éxito (creación normativa)`);

  // ── Lotes siguientes: PUT con reintento ───────────────────────────────
  for (let i = 1; i < anexosBatches.length; i++) {
    const batch = anexosBatches[i];

    const result = await executeWithRetry(
      () =>
        putNormativa({
          params: { ...formParams, id: newNormativa.id },
          images: { anexos: batch },
        }),
      i,
      batch,
    );

    if (!result.success) {
      failedBatches.push(result.failure);
    } else {
      console.log(`[BatchUpload] Lote ${i + 1} enviado con éxito`);
    }
  }

  // ── Verificación final ────────────────────────────────────────────────
  if (failedBatches.length > 0) {
    throw new BatchUploadError(failedBatches);
  }

  return newNormativa;
}

// ─────────────────────────────────────────────────────────────────────────

interface NormativaEditBatchParams {
  /** Parámetros del formulario (sin documentacion_adjunta ni anexos) */
  formParams: Record<string, any>;
  /** ID de la normativa a editar */
  normativaId: EntityID;
  /** Archivos de documentación adjunta NUEVOS (no virtuales) */
  nuevosDocAdjunta: File[];
  /** Archivos de anexos NUEVOS (no virtuales) */
  nuevosAnexos: File[];
  /** IDs de documentación a eliminar */
  documentacionEliminar: EntityID[];
}

/**
 * Edita una normativa subiendo los nuevos anexos por lotes.
 *
 * Lote 1 (PUT): params + id + eliminaciones + doc_adjunta nuevo + primeros 5 anexos
 * Lote N (PUT): params + id + siguientes 5 anexos
 *
 * Cada lote (a partir del 2) tiene 1 reintento automático.
 */
export async function editNormativaBatch({
  formParams,
  normativaId,
  nuevosDocAdjunta,
  nuevosAnexos,
  documentacionEliminar,
}: NormativaEditBatchParams): Promise<NormativaI> {
  const anexosBatches = chunkArray(nuevosAnexos, NORMATIVA_BATCH_SIZE_ANEXOS);
  const failedBatches: FailedBatch[] = [];

  // ── Lote 1: PUT con eliminaciones + primeros archivos (sin retry) ─────
  const firstImages: NormativaAdjuntos = {};
  if (nuevosDocAdjunta.length > 0) firstImages.documentacion_adjunta = nuevosDocAdjunta;
  if (anexosBatches.length > 0 && anexosBatches[0].length > 0) {
    firstImages.anexos = anexosBatches[0];
  }

  const updatedNormativa = await putNormativa({
    params: {
      ...formParams,
      id: normativaId,
      documentacion_eliminar: documentacionEliminar,
    },
    images: Object.keys(firstImages).length > 0 ? firstImages : undefined,
  });
  console.log(`[BatchUpload] Lote 1 enviado con éxito (edición normativa)`);

  // ── Lotes siguientes: PUT con reintento ───────────────────────────────
  for (let i = 1; i < anexosBatches.length; i++) {
    const batch = anexosBatches[i];

    const result = await executeWithRetry(
      () =>
        putNormativa({
          params: { ...formParams, id: normativaId },
          images: { anexos: batch },
        }),
      i,
      batch,
    );

    if (!result.success) {
      failedBatches.push(result.failure);
    } else {
      console.log(`[BatchUpload] Lote ${i + 1} enviado con éxito`);
    }
  }

  // ── Verificación final ────────────────────────────────────────────────
  if (failedBatches.length > 0) {
    throw new BatchUploadError(failedBatches);
  }

  return updatedNormativa;
}

// ═══════════════════════════════════════════════════════════════════════════
//  DISEÑOS CURRICULARES
// ═══════════════════════════════════════════════════════════════════════════

interface DisenioCurricularCreateBatchParams {
  /** Parámetros del formulario (sin documentacion_adjunta) */
  formParams: Record<string, any>;
  /** Todos los documentos nuevos a subir */
  documentos: File[];
}

/**
 * Crea un diseño curricular subiendo los documentos por lotes.
 *
 * Lote 1 (POST): params + primeros 10 documentos → crea la entidad
 * Lote N (PUT):  params + id + siguientes 10 documentos → agrega al existente
 *
 * Cada lote (a partir del 2) tiene 1 reintento automático.
 */
export async function createDisenioCurricularBatch({
  formParams,
  documentos,
}: DisenioCurricularCreateBatchParams): Promise<DiseñoCurricularI> {
  const docsBatches = chunkArray(documentos, DISENIO_BATCH_SIZE_DOCS);
  const failedBatches: FailedBatch[] = [];

  // ── Lote 1: POST (sin retry: si falla no hay entidad) ─────────────────
  const firstBatch = docsBatches.length > 0 ? docsBatches[0] : [];

  const newDiseño = await postDiseñoCurricular({
    params: formParams,
    images: { documentacion_adjunta: firstBatch },
  });
  console.log(`[BatchUpload] Lote 1 enviado con éxito (creación diseño curricular)`);

  // ── Lotes siguientes: PUT con reintento ───────────────────────────────
  for (let i = 1; i < docsBatches.length; i++) {
    const batch = docsBatches[i];

    const result = await executeWithRetry(
      () =>
        putDiseñoCurricular({
          params: { ...formParams, id: newDiseño.id },
          images: { documentacion_adjunta: batch },
        }),
      i,
      batch,
    );

    if (!result.success) {
      failedBatches.push(result.failure);
    } else {
      console.log(`[BatchUpload] Lote ${i + 1} enviado con éxito`);
    }
  }

  // ── Verificación final ────────────────────────────────────────────────
  if (failedBatches.length > 0) {
    throw new BatchUploadError(failedBatches);
  }

  return newDiseño;
}

// ─────────────────────────────────────────────────────────────────────────

interface DisenioCurricularEditBatchParams {
  /** Parámetros del formulario (sin documentacion_adjunta) */
  formParams: Record<string, any>;
  /** ID del diseño curricular a editar */
  disenoId: EntityID;
  /** Documentos NUEVOS (no virtuales) a subir */
  nuevosDocumentos: File[];
  /** IDs de documentación a eliminar */
  documentacionEliminar: EntityID[];
}

/**
 * Edita un diseño curricular subiendo nuevos documentos por lotes.
 *
 * Lote 1 (PUT): params + id + eliminaciones + primeros 10 documentos
 * Lote N (PUT): params + id + siguientes 10 documentos
 *
 * Cada lote (a partir del 2) tiene 1 reintento automático.
 */
export async function editDisenioCurricularBatch({
  formParams,
  disenoId,
  nuevosDocumentos,
  documentacionEliminar,
}: DisenioCurricularEditBatchParams): Promise<DiseñoCurricularI> {
  const docsBatches = chunkArray(nuevosDocumentos, DISENIO_BATCH_SIZE_DOCS);
  const failedBatches: FailedBatch[] = [];

  // ── Lote 1: PUT con eliminaciones + primeros documentos (sin retry) ───
  const firstImages: DisenioCurricularAdjuntos = {};
  if (docsBatches.length > 0 && docsBatches[0].length > 0) {
    firstImages.documentacion_adjunta = docsBatches[0];
  }

  const updatedDiseño = await putDiseñoCurricular({
    params: {
      ...formParams,
      id: disenoId,
      documentacion_eliminar: documentacionEliminar,
    },
    images: Object.keys(firstImages).length > 0 ? firstImages : undefined,
  });
  console.log(`[BatchUpload] Lote 1 enviado con éxito (edición diseño curricular)`);

  // ── Lotes siguientes: PUT con reintento ───────────────────────────────
  for (let i = 1; i < docsBatches.length; i++) {
    const batch = docsBatches[i];

    const result = await executeWithRetry(
      () =>
        putDiseñoCurricular({
          params: { ...formParams, id: disenoId },
          images: { documentacion_adjunta: batch },
        }),
      i,
      batch,
    );

    if (!result.success) {
      failedBatches.push(result.failure);
    } else {
      console.log(`[BatchUpload] Lote ${i + 1} enviado con éxito`);
    }
  }

  // ── Verificación final ────────────────────────────────────────────────
  if (failedBatches.length > 0) {
    throw new BatchUploadError(failedBatches);
  }

  return updatedDiseño;
}
